/**
 * Shared (server + client) rule for which virtual room a player is in. SC-416.
 *
 * A player is in a room when their avatar's collision body — the part of the sprite that
 * actually stands on the map — sits on that room's painted tiles. The client uses it to declare
 * entry and leave, and the server uses it for `serverVirtualRoomId`, occupancy and guest zones.
 * There is one implementation, so the two sides cannot disagree about the same position.
 *
 * Why the body: rooms are painted including their walls, and walls are collidable. Any test point
 * above the body (the sprite origin, or a "feet" box higher than the real feet) is inside the wall
 * row while a player in the corridor is pressed against the wall's far side, so they counted as
 * being in the room (SC-416). Approached from inside, the same offset dropped players at the top
 * wall, where the whiteboards are (SC-342). The body can never overlap a collidable tile, so a
 * painted wall cannot place anyone in a room from either side.
 */

export const VIRTUAL_ROOM_TILE_PX = 32

/** Every avatar spritesheet is 32×48 (client/src/scenes/Bootstrap.ts), drawn at origin 0.5. */
export const AVATAR_FRAME_WIDTH_PX = 32
export const AVATAR_FRAME_HEIGHT_PX = 48

const AVATAR_COLLISION_SCALE_X = 0.5
const AVATAR_COLLISION_SCALE_Y = 0.2

/**
 * The arcade body for an avatar sprite, in Phaser's setSize/setOffset terms (offset from the
 * frame's top-left). MyPlayer and OtherPlayer build their bodies from this, and membership is
 * measured against the same box, so the two cannot drift apart.
 */
export function avatarCollisionBody(frameWidth: number, frameHeight: number) {
  const width = frameWidth * AVATAR_COLLISION_SCALE_X
  const height = frameHeight * AVATAR_COLLISION_SCALE_Y
  return { width, height, offsetX: (frameWidth - width) / 2, offsetY: frameHeight - height }
}

const BODY = avatarCollisionBody(AVATAR_FRAME_WIDTH_PX, AVATAR_FRAME_HEIGHT_PX)

/** The body relative to the sprite origin (x, y): x−8…x+8, y+14.4…y+24. */
export const AVATAR_BODY_BOX = {
  left: BODY.offsetX - AVATAR_FRAME_WIDTH_PX / 2,
  right: BODY.offsetX - AVATAR_FRAME_WIDTH_PX / 2 + BODY.width,
  top: BODY.offsetY - AVATAR_FRAME_HEIGHT_PX / 2,
  bottom: BODY.offsetY - AVATAR_FRAME_HEIGHT_PX / 2 + BODY.height,
} as const

/**
 * Entering a room needs 30% of the body on its tiles. Staying needs 10%, so a player standing on
 * the boundary does not flicker in and out.
 */
export const VIRTUAL_ROOM_ENTER_OVERLAP = 0.3
export const VIRTUAL_ROOM_STAY_OVERLAP = 0.1

/**
 * The room a player at sprite position (x, y) is in, or null in the open world.
 *
 * `roomAtTile` answers "which room is painted on this tile" from whichever tile data the caller
 * has. `previousRoomId` is the room the player is currently in; that room uses the lower stay
 * threshold.
 *
 * Positions are rounded first because that is what the client sends the server
 * (PositionHandler), so the local player's own answer and the server's answer for them are
 * computed from the same integers. Overlap is measured in pixels rather than whole tiles, so a
 * body that grazes a tile by a rounding pixel does not count as standing on it.
 */
export function resolveVirtualRoomIdAtBody(
  x: number,
  y: number,
  roomAtTile: (tileX: number, tileY: number) => string | null | undefined,
  previousRoomId?: string | null,
): string | null {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null
  const px = Math.round(x)
  const py = Math.round(y)
  const minX = px + AVATAR_BODY_BOX.left
  const maxX = px + AVATAR_BODY_BOX.right
  const minY = py + AVATAR_BODY_BOX.top
  const maxY = py + AVATAR_BODY_BOX.bottom
  const bodyArea = (maxX - minX) * (maxY - minY)
  const T = VIRTUAL_ROOM_TILE_PX

  const overlapByRoom = new Map<string, number>()
  for (let tileX = Math.floor(minX / T); tileX < Math.ceil(maxX / T); tileX++) {
    for (let tileY = Math.floor(minY / T); tileY < Math.ceil(maxY / T); tileY++) {
      const roomId = roomAtTile(tileX, tileY)
      if (!roomId) continue
      const w = Math.min(maxX, (tileX + 1) * T) - Math.max(minX, tileX * T)
      const h = Math.min(maxY, (tileY + 1) * T) - Math.max(minY, tileY * T)
      if (w <= 0 || h <= 0) continue
      overlapByRoom.set(roomId, (overlapByRoom.get(roomId) ?? 0) + w * h)
    }
  }

  // Ties go to the current room, then to the lower id, so the answer never depends on the order
  // the caller's tile data happens to be in.
  let best: string | null = null
  let bestRatio = 0
  for (const [roomId, overlap] of overlapByRoom) {
    const ratio = overlap / bodyArea
    const threshold = roomId === previousRoomId ? VIRTUAL_ROOM_STAY_OVERLAP : VIRTUAL_ROOM_ENTER_OVERLAP
    if (ratio < threshold) continue
    const wins =
      best === null ||
      ratio > bestRatio ||
      (ratio === bestRatio && best !== previousRoomId && (roomId === previousRoomId || roomId < best))
    if (wins) {
      best = roomId
      bestRatio = ratio
    }
  }
  return best
}

/**
 * Sprite position that puts the avatar's body centred on world point (x, y). Use it when the
 * server places a player inside a room. A sprite placed at a tile's centre has its body near the
 * bottom of that tile and partly on the tile below, so a player put at the centre of a door tile
 * would be standing in the corridor.
 */
export function spritePositionStandingAt(x: number, y: number): { x: number; y: number } {
  const bodyCenterY = (AVATAR_BODY_BOX.top + AVATAR_BODY_BOX.bottom) / 2
  return { x: Math.round(x), y: Math.round(y - bodyCenterY) }
}
